<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useQuery } from '@vue/apollo-composable'
import {
  OperatorStepTreeDataQueryVariables,
  Query,
  StepTreeDataQuery,
  UpdateStep,
} from '@/generated/graphql'
import gql from 'graphql-tag'
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import ViewStep, { StepDraft } from '@/components/step/ViewStep.vue'
import OperatorModuleStepper from '@/components/operator/OperatorModuleStepper.vue'
import { OperatorModuleViewNode } from '@/views/organization/operator/OperatorModuleView.vue'
import useTree from '@/composables/useTree'
import useOperatorFontSize from '@/composables/useOperatorFontSize'

const props = defineProps<{
  stepId?: string
  bopId?: string
  moduleId?: string
  draftStepId?: string
  draft?: Partial<UpdateStep>
}>()
const emit = defineEmits<{
  selectStepId: [value: string]
}>()
const isOpen = defineModel<boolean>({ required: true })

const { t } = useI18n()
const { scale: fontScale } = useOperatorFontSize()

const inModule = computed(() => !!props.bopId && !!props.moduleId)

const bodyEl = ref<HTMLElement>()
const previewStepId = ref(props.stepId)
watch(isOpen, (open) => {
  if (open) previewStepId.value = props.stepId
})

// The browser re-applies the old offset as the incoming step's content loads in, so hold the top
// until it settles or the reader scrolls themselves.
let holdTopUntil = 0
let bodyObserver: ResizeObserver | undefined
const releaseHold = () => (holdTopUntil = 0)
watch(bodyEl, (el) => {
  bodyObserver?.disconnect()
  bodyObserver = undefined
  if (!el) return
  bodyObserver = new ResizeObserver(() => {
    if (performance.now() < holdTopUntil) el.scrollTop = 0
  })
  bodyObserver.observe(el)
  if (el.firstElementChild) bodyObserver.observe(el.firstElementChild)
})
onBeforeUnmount(() => bodyObserver?.disconnect())

const onSelectStepId = (id: string | undefined) => {
  if (!id || id === previewStepId.value) return
  previewStepId.value = id
  emit('selectStepId', id)
  holdTopUntil = performance.now() + 1000
  nextTick(() => bodyEl.value?.scrollTo({ top: 0 }))
}

const treeQuery = useQuery<
  { product: { module: StepTreeDataQuery['product']['module'] } },
  Omit<OperatorStepTreeDataQueryVariables, 'bopId'>
>(
  gql`
    query StepPreviewStepTree($moduleId: ID!) {
      product {
        module(id: $moduleId) {
          id
          abbreviation
          nodes {
            id
            parentId
            step {
              id
              name
              durationInSeconds
              stepType {
                id
                name
              }
            }
          }
        }
      }
    }
  `,
  () => ({ moduleId: props.moduleId ?? '' }),
  () => ({ enabled: isOpen.value && inModule.value }),
)
const { treeRootNodes } = useTree()
const treeNodes = computed(() =>
  treeRootNodes(
    computed<OperatorModuleViewNode[]>(() => treeQuery.result.value?.product.module.nodes || []),
    false,
  ),
)

const stepTypesQuery = useQuery<Query>(
  gql`
    query StepPreviewStepTypes {
      product {
        stepTypes {
          id
          name
        }
      }
    }
  `,
  {},
  () => ({ enabled: isOpen.value }),
)
const draftStepTypeName = computed(
  () =>
    stepTypesQuery.result.value?.product.stepTypes.find((s) => s.id === props.draft?.stepTypeId)
      ?.name,
)
const viewDraft = computed<StepDraft | undefined>(() =>
  props.draft
    ? {
        name: props.draft.name ?? undefined,
        stepTypeName: draftStepTypeName.value,
        instructionText: props.draft.instructionText ?? undefined,
      }
    : undefined,
)
</script>

<template>
  <v-dialog v-model="isOpen" width="65%" height="85vh">
    <div class="preview-shell bg-surface">
      <div class="preview-header bg-grey-lighten-4">
        <div class="preview-header-text">
          <strong>{{ t('component.stepPreviewDialog.title') }}</strong>
          <span class="text-medium-emphasis text-body-2">
            {{ t('component.stepPreviewDialog.hint') }}
          </span>
        </div>
        <v-btn
          icon="close"
          density="comfortable"
          variant="text"
          :title="t('button.close')"
          @click="isOpen = false"
        />
      </div>

      <v-divider />

      <div ref="bodyEl" class="preview-body" @wheel="releaseHold" @pointerdown="releaseHold">
        <operator-module-stepper
          v-if="inModule"
          :bop-id="props.bopId as string"
          :module-id="props.moduleId as string"
          :step-id="previewStepId"
          :nodes="treeNodes"
          :draft-step-id="props.draftStepId"
          :draft="viewDraft"
          preview
          @select-step-id="onSelectStepId"
        />

        <v-card v-else :style="{ '--operator-font-scale': fontScale }">
          <v-card-text>
            <view-step v-if="stepId" :step-id="stepId" :draft="viewDraft" />
          </v-card-text>
        </v-card>
      </div>
    </div>
  </v-dialog>
</template>

<style scoped lang="scss">
.preview-shell {
  display: flex;
  flex-direction: column;
  height: 100%;
  border-radius: 4px;
  overflow: hidden;
}
.preview-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 16px;
  flex: none;
}
.preview-header-text {
  display: flex;
  flex-direction: column;
}
.preview-body {
  flex: 1;
  min-height: 0;
  overflow-y: scroll;
  scrollbar-gutter: stable;
}

.preview-body > :deep(.v-card) {
  border-radius: 0;
  box-shadow: none;
}
</style>
